import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { filter, throttleTime } from 'rxjs/operators';
import { BaseCalendarModel } from '../../../../core/models/calendar/base-calendar.model';
import { MonthCalendarModel } from '../../../../core/models/calendar/month-calendar.model';
import { SidebarService } from '../../../../core/services/ui/sidebar.service';
import { CalendarEvents, CalendarViews } from '../../calendar-types';
import { TimeBlockDurationCalculationService } from '../../components/time-block/calculation/duration/time-block-duration-calculation.service';
import { ParallelTimeBlockGeometryCalculatorService } from '../../components/time-block/calculation/geometry/parallel-time-block-geometry-calculator.service';
import { TimeBlockCrudService } from '../../components/time-block/crud/time-block-crud.service';
import { TimeBlockMonthViewContainerService } from '../../components/time-block/services/time-block-month-view-container.service';
import { TimeBlockHookContainer } from '../../components/time-block/types/time-block-types';
import { CalendarScrollbarService } from '../../services/calendar-scrollbar.service';
import { CalendarServiceHelper } from '../../services/calendar-service-helper';
import { CalendarViewHandlerService } from '../../services/calendar-view-handler.service';
import { CalendarService } from '../../services/calendar.service';
import { TimeCoordinateMappingService } from '../../time-mapping/time-coordinate-mapping.service';
import { WINDOW } from '../../util/browser-window-ref';
import {
  CssDropzone,
  FixIntervalTimeBlockHeightPx,
  ShowMoreButtonCSSSelector,
} from '../../util/calendar-style.constants';
import { MonthViewHandler } from '../handlers/month-view-handler';
import { TimeBlockContainerDirective } from '../time-block-container.directive';
import { TimeBlockInjectable } from '../time-block-injectable';

@Component({
  selector: 'app-month-view',
  templateUrl: './month-view.component.html',
  standalone: false,
})
export class MonthViewComponent
  extends MonthViewHandler
  implements TimeBlockInjectable, OnInit, AfterViewInit, OnDestroy
{
  // A lane is a table cell in the month view.
  @ViewChildren(TimeBlockContainerDirective)
  public timeLanePlaceholders!: QueryList<TimeBlockContainerDirective>;
  // Calendar body
  @ViewChild('htmlItemContainer') public htmlItemContainer: ElementRef<HTMLElement>;
  // A lane (i.e. a bucket)TimeBlockMonthViewContainerService is a table cell in the month view.
  @ViewChildren('bucket', { read: ElementRef }) public buckets: QueryList<ElementRef>;
  public accumulatedTimeMap = new Map<string, number>();
  public CalendarView = CalendarViews;
  public readonly FixIntervalTimeBlockHeightPx = FixIntervalTimeBlockHeightPx;
  public readonly CssDropzone = CssDropzone;
  public readonly ShowMoreButtonCSSSelector = ShowMoreButtonCSSSelector;
  public readonly String = String;

  // DI
  private readonly window = inject<Window>(WINDOW);

  constructor(
    public calendarService: CalendarService,
    public readonly timeBlockMonthViewContainerService: TimeBlockMonthViewContainerService,
    private readonly calendarViewHandlerService: CalendarViewHandlerService,
    private readonly timeBlockCrudService: TimeBlockCrudService,
    private readonly timeCoordinateMappingService: TimeCoordinateMappingService,
    private readonly calendarScrollbarService: CalendarScrollbarService,
    private readonly parallelTimeBlockGeometryCalculatorService: ParallelTimeBlockGeometryCalculatorService,
    private readonly timeBlockDurationCalculationService: TimeBlockDurationCalculationService,
    private readonly cd: ChangeDetectorRef,
    private readonly sidebarService: SidebarService,
  ) {
    super(
      calendarService,
      timeBlockCrudService,
      timeCoordinateMappingService,
      sidebarService,
      calendarViewHandlerService,
    );
  }

  ngOnInit(): void {
    this.initViewportResize();
  }

  ngAfterViewInit(): void {
    this.cd.detach();

    this.window.setTimeout(() => {
      this.initCalendarView();
    });

    const calendarModel = new MonthCalendarModel();
    Object.assign(calendarModel, this.calendarService.model);

    this.calendarService.emitCalendarChange(calendarModel, CalendarEvents.InitializedCalendarView);

    this.loadTimeBlocks();

    const targetEvents = [CalendarEvents.CalculatedWorkingTimeDurations];
    const callback = (
      _: BaseCalendarModel,
      calendarEvents: CalendarEvents[],
      meta: unknown,
    ): void => {
      if (calendarEvents.includes(CalendarEvents.CalculatedWorkingTimeDurations)) {
        this.accumulatedTimeMap = meta as Map<string, number>;
        this.cd.detectChanges();
      }
    };

    this.subs.sink = CalendarServiceHelper.calendarModelUpdated(
      this.calendarService,
      callback,
      targetEvents,
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.destroy();
  }

  protected setModelTemplateData(): void {
    const updatedMonthCalendarModel = this.calendarService.model as MonthCalendarModel;
    updatedMonthCalendarModel.timeBlockContainers = this.timeLanePlaceholders.map((e) => {
      e.viewContainerRef.clear();
      const hookContainer: TimeBlockHookContainer = {
        vcr: e.viewContainerRef,
        cd: e.changeDetectorRef,
      };
      return hookContainer;
    });
    updatedMonthCalendarModel.timeBlockHtmlContainers = this.buckets.toArray();
    // Init full day month calendar model
    updatedMonthCalendarModel.fixIntervalCalendarModel.timeBlockContainers =
      updatedMonthCalendarModel.timeBlockContainers;
    updatedMonthCalendarModel.fixIntervalCalendarModel.timeBlockHtmlContainers =
      updatedMonthCalendarModel.timeBlockHtmlContainers;
    // Emit calendar model
    this.calendarService.emitCalendarChange(updatedMonthCalendarModel, []);
  }

  protected initCalendarView(): void {
    if (!this.initialized || this.viewportSizeChanged) {
      this.cd.detectChanges();
      this.updateCalendarDimensions(
        this.htmlItemContainer,
        this.calendarScrollbarService.calendarBodyScrollbar.scrollData.scrollPosY,
      );
    }
  }

  private initViewportResize(): void {
    this.subs.sink = this.calendarService.calendarViewportResized$
      .pipe(throttleTime(500, undefined, { leading: true, trailing: true })) // Improves performance
      .subscribe(() => {
        this.timeBlockMonthViewContainerService.toggleOverflowingTimeBlocks();
        this.cd.detectChanges();
      });
  }

  private loadTimeBlocks() {
    this.subs.sink = this.timeBlockCrudService.timeBlocksFetched$
      .pipe(filter((timeBlocks) => timeBlocks !== null))
      .subscribe(([fixIntervalTimeBlockMap, variableIntervalTimeBlockMap]) => {
        this.cd.detectChanges();
        this.setModelTemplateData();

        const model = this.calendarService.model as MonthCalendarModel;
        this.insertTimeBlocksIntoView(
          fixIntervalTimeBlockMap,
          model.fixIntervalCalendarModel.timeBlocks,
          variableIntervalTimeBlockMap,
          model.timeBlocks,
        );

        // Set the ordering for full day time blocks and calendar body time blocks.
        this.parallelTimeBlockGeometryCalculatorService.updateParallelTimeBlockGeometries(null);
        this.calendarService.emitCalendarChange(model, CalendarEvents.RenderedTimeBlocks);
        // Hide vertical overflowing time blocks.
        this.timeBlockMonthViewContainerService.toggleOverflowingTimeBlocks();
        // Calculate time block duration
        this.timeBlockDurationCalculationService.calculateTimeBlockDurationSums$.next(null);
      });
  }
}
